import React, { useState } from 'react';
import styled from 'styled-components';

const SecurityContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TestCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
  }
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const IssueList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
`;

const IssueItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid ${props => props.severity === 'error' ? '#dc3545' : props.severity === 'warning' ? '#ffc107' : '#28a745'};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const SecurityHeadersTest = () => {
  const [testResults, setTestResults] = useState({
    headers: {
      hsts: null,
      xss: null,
      frame: null,
      content: null,
      referrer: null,
      permissions: null
    },
    ssl: {
      certificate: null,
      protocol: null,
      ciphers: null,
      hsts: null
    },
    csp: {
      policy: null,
      directives: null,
      report: null
    },
    rateLimit: {
      enabled: null,
      window: null,
      max: null
    },
    validation: {
      input: null,
      sanitization: null,
      encoding: null
    },
    protection: {
      xss: null,
      csrf: null,
      sql: null
    }
  });

  const [issues, setIssues] = useState([]);

  const checkSecurityHeaders = async () => {
    const results = {
      hsts: { supported: false, value: null },
      xss: { supported: false, value: null },
      frame: { supported: false, value: null },
      content: { supported: false, value: null },
      referrer: { supported: false, value: null },
      permissions: { supported: false, value: null }
    };

    try {
      const response = await fetch(window.location.origin);
      const headers = response.headers;

      // Check HSTS
      results.hsts = {
        supported: headers.has('strict-transport-security'),
        value: headers.get('strict-transport-security')
      };

      // Check XSS Protection
      results.xss = {
        supported: headers.has('x-xss-protection'),
        value: headers.get('x-xss-protection')
      };

      // Check Frame Options
      results.frame = {
        supported: headers.has('x-frame-options'),
        value: headers.get('x-frame-options')
      };

      // Check Content Type
      results.content = {
        supported: headers.has('x-content-type-options'),
        value: headers.get('x-content-type-options')
      };

      // Check Referrer Policy
      results.referrer = {
        supported: headers.has('referrer-policy'),
        value: headers.get('referrer-policy')
      };

      // Check Permissions Policy
      results.permissions = {
        supported: headers.has('permissions-policy'),
        value: headers.get('permissions-policy')
      };
    } catch (error) {
      console.error('Error checking security headers:', error);
    }

    setTestResults(prev => ({ ...prev, headers: results }));

    const newIssues = [];
    if (!results.hsts.supported) {
      newIssues.push({ severity: 'error', message: 'HSTS header missing' });
    }
    if (!results.xss.supported) {
      newIssues.push({ severity: 'error', message: 'XSS Protection header missing' });
    }
    if (!results.frame.supported) {
      newIssues.push({ severity: 'error', message: 'Frame Options header missing' });
    }
    if (!results.content.supported) {
      newIssues.push({ severity: 'error', message: 'Content Type Options header missing' });
    }
    if (!results.referrer.supported) {
      newIssues.push({ severity: 'warning', message: 'Referrer Policy header missing' });
    }
    if (!results.permissions.supported) {
      newIssues.push({ severity: 'warning', message: 'Permissions Policy header missing' });
    }

    return newIssues;
  };

  const checkSSLConfig = async () => {
    const results = {
      certificate: { valid: false, info: null },
      protocol: { supported: false, version: null },
      ciphers: { strong: false, list: null },
      hsts: { enabled: false, maxAge: null }
    };

    try {
      // Check if HTTPS is being used
      results.certificate.valid = window.location.protocol === 'https:';
      
      // Check HSTS preload status
      const hstsHeader = document.querySelector('meta[http-equiv="Strict-Transport-Security"]');
      if (hstsHeader) {
        results.hsts.enabled = true;
        results.hsts.maxAge = hstsHeader.content;
      }
    } catch (error) {
      console.error('Error checking SSL configuration:', error);
    }

    setTestResults(prev => ({ ...prev, ssl: results }));

    const newIssues = [];
    if (!results.certificate.valid) {
      newIssues.push({ severity: 'error', message: 'HTTPS not enabled' });
    }
    if (!results.hsts.enabled) {
      newIssues.push({ severity: 'warning', message: 'HSTS preload not configured' });
    }

    return newIssues;
  };

  const checkCSP = async () => {
    const results = {
      policy: { present: false, value: null },
      directives: { complete: false, list: null },
      report: { enabled: false, uri: null }
    };

    try {
      const response = await fetch(window.location.origin);
      const headers = response.headers;
      
      // Check CSP header
      results.policy = {
        present: headers.has('content-security-policy'),
        value: headers.get('content-security-policy')
      };

      // Check CSP Report Only header
      results.report = {
        enabled: headers.has('content-security-policy-report-only'),
        uri: headers.get('content-security-policy-report-only')
      };
    } catch (error) {
      console.error('Error checking CSP:', error);
    }

    setTestResults(prev => ({ ...prev, csp: results }));

    const newIssues = [];
    if (!results.policy.present) {
      newIssues.push({ severity: 'error', message: 'Content Security Policy header missing' });
    }
    if (!results.report.enabled) {
      newIssues.push({ severity: 'warning', message: 'CSP reporting not configured' });
    }

    return newIssues;
  };

  const checkRateLimiting = async () => {
    const results = {
      enabled: false,
      window: null,
      max: null
    };

    try {
      // Attempt to make multiple requests quickly
      const requests = Array(5).fill().map(() => 
        fetch(window.location.origin)
      );
      
      const responses = await Promise.all(requests);
      const statusCodes = responses.map(r => r.status);
      
      // Check if any requests were rate limited (429 status)
      results.enabled = statusCodes.includes(429);
    } catch (error) {
      console.error('Error checking rate limiting:', error);
    }

    setTestResults(prev => ({ ...prev, rateLimit: results }));

    const newIssues = [];
    if (!results.enabled) {
      newIssues.push({ severity: 'warning', message: 'Rate limiting may not be configured' });
    }

    return newIssues;
  };

  const checkInputValidation = async () => {
    const results = {
      input: { sanitized: false, methods: null },
      sanitization: { implemented: false, library: null },
      encoding: { proper: false, types: null }
    };

    try {
      // Check for common input validation patterns
      const forms = document.querySelectorAll('form');
      const inputs = document.querySelectorAll('input, textarea, select');
      
      results.input.sanitized = inputs.length > 0;
      results.encoding.proper = true; // This would need server-side verification
    } catch (error) {
      console.error('Error checking input validation:', error);
    }

    setTestResults(prev => ({ ...prev, validation: results }));

    const newIssues = [];
    if (!results.input.sanitized) {
      newIssues.push({ severity: 'warning', message: 'Input validation may need improvement' });
    }

    return newIssues;
  };

  const checkProtection = async () => {
    const results = {
      xss: { protected: false, methods: null },
      csrf: { protected: false, token: null },
      sql: { protected: false, methods: null }
    };

    try {
      // Check for CSRF token in forms
      const forms = document.querySelectorAll('form');
      const hasCsrfToken = Array.from(forms).some(form => 
        form.querySelector('input[name*="csrf"]') || 
        form.querySelector('input[name*="token"]')
      );
      
      results.csrf.protected = hasCsrfToken;
      
      // Check for XSS protection headers
      const response = await fetch(window.location.origin);
      const headers = response.headers;
      results.xss.protected = headers.has('x-xss-protection');
    } catch (error) {
      console.error('Error checking protection measures:', error);
    }

    setTestResults(prev => ({ ...prev, protection: results }));

    const newIssues = [];
    if (!results.xss.protected) {
      newIssues.push({ severity: 'error', message: 'XSS protection measures incomplete' });
    }
    if (!results.csrf.protected) {
      newIssues.push({ severity: 'error', message: 'CSRF protection may be missing' });
    }
    if (!results.sql.protected) {
      newIssues.push({ severity: 'warning', message: 'SQL injection protection needs verification' });
    }

    return newIssues;
  };

  const runAllTests = async () => {
    setIssues([]);
    
    const headersIssues = await checkSecurityHeaders();
    const sslIssues = await checkSSLConfig();
    const cspIssues = await checkCSP();
    const rateLimitIssues = await checkRateLimiting();
    const validationIssues = await checkInputValidation();
    const protectionIssues = await checkProtection();

    setIssues([
      ...headersIssues,
      ...sslIssues,
      ...cspIssues,
      ...rateLimitIssues,
      ...validationIssues,
      ...protectionIssues
    ]);
  };

  const getStatusFromValue = (value) => {
    if (!value) return 'error';
    if (value.supported === false || value.valid === false || value.present === false) return 'error';
    if (value.proper === false || value.complete === false) return 'warning';
    return 'good';
  };

  return (
    <SecurityContainer>
      <TestSection>
        <h3>Security Headers and Protection Tests</h3>
        <TestButton onClick={runAllTests}>
          Run All Security Tests
        </TestButton>

        <TestResults>
          <h3>Security Headers</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.headers.hsts)}>
              <h4>HSTS</h4>
              <p>
                {testResults.headers.hsts ? 
                  `${testResults.headers.hsts.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.headers.xss)}>
              <h4>XSS Protection</h4>
              <p>
                {testResults.headers.xss ? 
                  `${testResults.headers.xss.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.headers.frame)}>
              <h4>Frame Options</h4>
              <p>
                {testResults.headers.frame ? 
                  `${testResults.headers.frame.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.headers.content)}>
              <h4>Content Type</h4>
              <p>
                {testResults.headers.content ? 
                  `${testResults.headers.content.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.headers.referrer)}>
              <h4>Referrer Policy</h4>
              <p>
                {testResults.headers.referrer ? 
                  `${testResults.headers.referrer.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.headers.permissions)}>
              <h4>Permissions Policy</h4>
              <p>
                {testResults.headers.permissions ? 
                  `${testResults.headers.permissions.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>SSL/TLS Configuration</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.ssl.certificate)}>
              <h4>SSL Certificate</h4>
              <p>
                {testResults.ssl.certificate ? 
                  `${testResults.ssl.certificate.valid ? 'Valid' : 'Invalid'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.ssl.protocol)}>
              <h4>Protocol Version</h4>
              <p>
                {testResults.ssl.protocol ? 
                  `${testResults.ssl.protocol.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.ssl.ciphers)}>
              <h4>Cipher Suites</h4>
              <p>
                {testResults.ssl.ciphers ? 
                  `${testResults.ssl.ciphers.strong ? 'Strong' : 'Weak'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.ssl.hsts)}>
              <h4>HSTS Preload</h4>
              <p>
                {testResults.ssl.hsts ? 
                  `${testResults.ssl.hsts.enabled ? 'Enabled' : 'Disabled'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Content Security Policy</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.csp.policy)}>
              <h4>CSP Header</h4>
              <p>
                {testResults.csp.policy ? 
                  `${testResults.csp.policy.present ? 'Present' : 'Missing'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.csp.directives)}>
              <h4>Directives</h4>
              <p>
                {testResults.csp.directives ? 
                  `${testResults.csp.directives.complete ? 'Complete' : 'Incomplete'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.csp.report)}>
              <h4>Reporting</h4>
              <p>
                {testResults.csp.report ? 
                  `${testResults.csp.report.enabled ? 'Enabled' : 'Disabled'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Rate Limiting</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.rateLimit.enabled)}>
              <h4>Rate Limiting</h4>
              <p>
                {testResults.rateLimit.enabled ? 
                  `${testResults.rateLimit.enabled ? 'Enabled' : 'Disabled'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.rateLimit.window)}>
              <h4>Time Window</h4>
              <p>
                {testResults.rateLimit.window ? 
                  `${testResults.rateLimit.window} seconds` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.rateLimit.max)}>
              <h4>Max Requests</h4>
              <p>
                {testResults.rateLimit.max ? 
                  `${testResults.rateLimit.max} requests` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Input Validation</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.validation.input)}>
              <h4>Input Sanitization</h4>
              <p>
                {testResults.validation.input ? 
                  `${testResults.validation.input.sanitized ? 'Sanitized' : 'Not Sanitized'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.validation.sanitization)}>
              <h4>Sanitization Methods</h4>
              <p>
                {testResults.validation.sanitization ? 
                  `${testResults.validation.sanitization.implemented ? 'Implemented' : 'Not Implemented'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.validation.encoding)}>
              <h4>Output Encoding</h4>
              <p>
                {testResults.validation.encoding ? 
                  `${testResults.validation.encoding.proper ? 'Proper' : 'Improper'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Protection Measures</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.protection.xss)}>
              <h4>XSS Protection</h4>
              <p>
                {testResults.protection.xss ? 
                  `${testResults.protection.xss.protected ? 'Protected' : 'Not Protected'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.protection.csrf)}>
              <h4>CSRF Protection</h4>
              <p>
                {testResults.protection.csrf ? 
                  `${testResults.protection.csrf.protected ? 'Protected' : 'Not Protected'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.protection.sql)}>
              <h4>SQL Injection</h4>
              <p>
                {testResults.protection.sql ? 
                  `${testResults.protection.sql.protected ? 'Protected' : 'Not Protected'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          {issues.length > 0 && (
            <>
              <h3>Security Issues</h3>
              <IssueList>
                {issues.map((issue, index) => (
                  <IssueItem key={index} severity={issue.severity}>
                    {issue.message}
                  </IssueItem>
                ))}
              </IssueList>
            </>
          )}
        </TestResults>
      </TestSection>
    </SecurityContainer>
  );
};

export default SecurityHeadersTest; 